import { ApiError, type ApiClient } from "../api/client";
import type { Booking, DemoLink, EmbedSettings, Me } from "../api/types";

const DAY_MS = 86_400_000;
const OWNER = "alex@acme-analytics.example.com";

/** A time `days` from `now`, at a fixed UTC hour, so sample dates always look current. */
function at(now: number, days: number, hourUtc = 9, minuteUtc = 0): string {
  const date = new Date(now + days * DAY_MS);
  date.setUTCHours(hourUtc, minuteUtc, 0, 0);
  return date.toISOString();
}

export interface TourData {
  me: Me;
  links: DemoLink[];
  embed: EmbedSettings;
  bookings: Booking[];
}

/** A made-up customer workspace. Every name, company and address here is fictional. */
export function tourData(siteUrl: string, now: number): TourData {
  const allowedOrigins = ["https://www.acme-analytics.example.com", "https://app.acme-analytics.example.com"];
  const embedKey = "ek_acmeSample7Qm2xVb9Lp4RtW8n";

  const link = (overrides: Partial<DemoLink> & Pick<DemoLink, "link_id" | "recipient_email">): DemoLink => ({
    recipient_name: null,
    note: null,
    status: "active",
    created_by: OWNER,
    created_at: at(now, -1),
    expires_at: at(now, 6),
    last_sent_at: null,
    open_count: 0,
    ...overrides,
  });

  return {
    me: {
      user: { id: "tour-user", email: OWNER, is_admin: false },
      tenant: {
        tenant_id: "acme",
        name: "Acme Analytics",
        status: "ready",
        allowed_origins: allowedOrigins,
        team_emails: [OWNER],
        created_at: at(now, -40),
        updated_at: at(now, -3),
      },
    },
    links: [
      link({
        link_id: "lk_tour_1",
        recipient_email: "maya.chen@northwind.example.com",
        recipient_name: "Maya Chen",
        note: "Following up on our chat. Here's a live walkthrough of Acme.",
        created_at: at(now, -2, 6),
        expires_at: at(now, 5, 6),
        last_sent_at: at(now, -2, 6),
        open_count: 3,
      }),
      link({
        link_id: "lk_tour_2",
        recipient_email: "daniel.okafor@brightpath.example.com",
        recipient_name: "Daniel Okafor",
        created_at: at(now, -1, 14),
        expires_at: at(now, 13, 14),
        last_sent_at: at(now, -1, 14),
        open_count: 1,
      }),
      link({
        link_id: "lk_tour_3",
        recipient_email: "sofia.rossi@lumenhealth.example.com",
        recipient_name: "Sofia Rossi",
        created_at: at(now, -6, 10),
        expires_at: at(now, 1, 10),
        last_sent_at: at(now, -6, 10),
      }),
      link({
        link_id: "lk_tour_4",
        recipient_email: "ops@fabrikam.example.com",
        created_at: at(now, -10, 8),
        expires_at: at(now, -3, 8),
        open_count: 2,
      }),
      link({
        link_id: "lk_tour_5",
        recipient_email: "jpark@contoso.example.com",
        recipient_name: "James Park",
        status: "revoked",
        created_at: at(now, -9, 12),
        expires_at: at(now, -2, 12),
      }),
    ],
    embed: {
      tenant_id: "acme",
      tenant_name: "Acme Analytics",
      status: "ready",
      ready: true,
      embed_key: embedKey,
      snippet: `<script src="${siteUrl.replace(/\/+$/, "")}/embed.js" data-trylook-key="${embedKey}" async></script>`,
      allowed_origins: allowedOrigins,
    },
    bookings: [
      {
        booking_id: "bk_tour_1",
        tenant_id: "acme",
        session_id: "ds_tour_1",
        prospect_name: "Maya Chen",
        prospect_email: "maya.chen@northwind.example.com",
        company: "Northwind Traders",
        starts_at: at(now, 2, 10),
        duration_minutes: 30,
        timezone: "Asia/Kolkata",
        summary:
          "Runs a 12-person SDR team that can't keep up with inbound demo requests. Wants the guide on the pricing page before their Q4 launch.",
        qualification: {
          role: "Head of Sales",
          team_size: "12 SDRs",
          use_case: "Demos for inbound pricing-page leads",
          timeline: "Before Q4 launch",
        },
        created_at: at(now, -1, 7),
      },
      {
        booking_id: "bk_tour_2",
        tenant_id: "acme",
        session_id: "ds_tour_2",
        prospect_name: "Daniel Okafor",
        prospect_email: "daniel.okafor@brightpath.example.com",
        company: "BrightPath",
        starts_at: at(now, 3, 15),
        duration_minutes: 45,
        timezone: "America/New_York",
        summary:
          "Solutions engineers spend half their week on first demos. Interested in personal demo links for new trial sign-ups.",
        qualification: {
          role: "VP Revenue",
          team_size: "4 solutions engineers",
          use_case: "First demos for trial sign-ups",
          current_tools: "HubSpot, Zoom",
        },
        created_at: at(now, -1, 16),
      },
      {
        booking_id: "bk_tour_3",
        tenant_id: "acme",
        session_id: "ds_tour_3",
        prospect_name: "Sofia Rossi",
        prospect_email: "sofia.rossi@lumenhealth.example.com",
        company: "Lumen Health",
        starts_at: at(now, 6, 8, 30),
        duration_minutes: 30,
        timezone: "Europe/Rome",
        summary:
          "Sells across Europe and the US. Wants demos available around the clock, with security questions handed to her team.",
        qualification: {
          role: "Sales Director",
          use_case: "After-hours demos across time zones",
          timeline: "This quarter",
        },
        created_at: at(now, -2, 11),
      },
    ],
  };
}

/** The dashboard API for the product tour: real pages, sample workspace, nothing can be changed. */
export function createTourApi(siteUrl: string, now: number = Date.now()): ApiClient {
  const data = tourData(siteUrl, now);
  const readOnly = () =>
    Promise.reject(
      new ApiError({ status: 403, code: "tour_read_only", message: "This is a sample workspace for the product tour." }),
    );

  return {
    startSession: readOnly,
    describeLink: readOnly,
    startLinkSession: readOnly,
    me: () => Promise.resolve(data.me),
    listLinks: () => Promise.resolve({ links: data.links }),
    createLink: readOnly,
    resendLink: readOnly,
    revokeLink: readOnly,
    getEmbed: () => Promise.resolve(data.embed),
    updateOrigins: readOnly,
    listBookings: () => Promise.resolve({ bookings: data.bookings }),
    listTenants: () => Promise.resolve({ tenants: [] }),
    createTenant: readOnly,
    updateTenant: readOnly,
  };
}
